import { useState } from "react";
import type { RunLine } from "../../../api/m5";
import { money } from "../../../api/periods";
import { Button, TextInput } from "../../../components/ui";

type Ded = { label: string; amount: string };
const DEFAULTS = ["Federal income tax withholding", "Social Security", "Medicare", "Other"];
const cents = (s: string) => Math.round(Number(s || 0) * 100);

export function NetEntryForm({ line, onSave }: { line: RunLine; onSave: (body: Record<string, unknown>) => Promise<void> }) {
  const n = line.net;
  const [src, setSrc] = useState(n?.source_name ?? "");
  const [ref, setRef] = useState(n?.reference ?? "");
  const [ded, setDed] = useState<Ded[]>(n ? n.deductions.map((d) => ({ label: d.label, amount: (d.amount_cents / 100).toFixed(2) })) : DEFAULTS.map((l) => ({ label: l, amount: "" })));
  const [diff, setDiff] = useState(n ? (n.difference_cents / 100).toFixed(2) : "");
  const [why, setWhy] = useState(n?.difference_reason ?? "");
  const [net, setNet] = useState(n ? (n.net_pay_cents / 100).toFixed(2) : "");
  const id = line.employee_id;
  const save = () => onSave({ source_name: src, reference: ref, deductions: ded.filter((d) => d.amount !== "").map((d) => ({ label: d.label, amount_cents: cents(d.amount) })),
    difference_cents: cents(diff), difference_reason: why || null, net_pay_cents: cents(net) });
  return (
    <div className="mt-3 space-y-2 rounded-sm border border-white/10 bg-ink p-3 text-xs" data-testid={`net-form-${id}`}>
      <p className="text-zinc-400">Type figures exactly as on the payroll provider/accountant stub. Nothing is calculated here. Total gross: <strong className="font-mono text-white">{money(line.total_gross_cents)}</strong></p>
      <div className="flex flex-wrap gap-2">
        <TextInput className="w-40" placeholder="Source (e.g. Accountant)" value={src} onChange={(e) => setSrc(e.target.value)} data-testid={`net-source-${id}`} />
        <TextInput className="w-48" placeholder="Stub / run reference" value={ref} onChange={(e) => setRef(e.target.value)} data-testid={`net-ref-${id}`} />
      </div>
      {ded.map((d, i) => (
        <div key={i} className="flex gap-2">
          <TextInput className="w-64" value={d.label} onChange={(e) => setDed(ded.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} data-testid={`net-ded-label-${id}-${i}`} />
          <TextInput className="w-28" placeholder="$0.00" value={d.amount} onChange={(e) => setDed(ded.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))} data-testid={`net-ded-amount-${id}-${i}`} />
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        <TextInput className="w-40" placeholder="± reconciling diff $" value={diff} onChange={(e) => setDiff(e.target.value)} data-testid={`net-diff-${id}`} />
        <TextInput className="w-64" placeholder="Reason (required if diff)" value={why} onChange={(e) => setWhy(e.target.value)} data-testid={`net-diff-reason-${id}`} />
        <TextInput className="w-32" placeholder="Net pay $" value={net} onChange={(e) => setNet(e.target.value)} data-testid={`net-pay-${id}`} />
        <Button onClick={save} disabled={!src.trim() || !ref.trim() || net === ""} data-testid={`net-save-${id}`}>Save net pay</Button>
      </div>
    </div>
  );
}
